import { create } from 'zustand';

import type { CapabilityMap } from './capabilities';

/**
 * The session slice. **No token is ever persisted by this repo** — the access
 * token lives here, in memory, and dies with the tab; the refresh token is an
 * HttpOnly host-only cookie this bundle neither reads, writes, nor names
 * (PRD-TDD §11.1).
 *
 * Entitlement state, publish state and the caller's role are *server* state
 * read through the query cache. The role below is the bootstrap answer the
 * guard routes on, never an authorization decision — every gated call is
 * re-checked by the owning service on the request itself.
 */

/** `backend-auth__roles` — architecture §6. `anonymous` never occupies a row. */
export type AccountRole = 'user' | 'operator' | 'admin';

/**
 * The profile head, as `ModelProfileHeadForClient` serves it — mapped to the
 * repo's camelCase at the one boundary that reads the wire.
 *
 * `biography` and `email` are **absent** rather than null for an account that
 * carries neither: the service dumps its response models with `exclude_none`,
 * and a surface renders the field if and only if the key is there. The mobile
 * number is deliberately not here — the login response does not carry it, and
 * inventing a slot for it would make the store claim a value no read fills.
 */
export interface SessionProfile {
  id: string;
  displayName: string;
  handle: string;
  email?: string;
  biography?: string;
  avatarUrl?: string;
}

export interface SessionState {
  /** `null` until the first bootstrap resolves. */
  profile: SessionProfile | null;
  role: AccountRole | null;
  /** `service: role_name` — a UI hint from `backend-auth`, never a permission. */
  adminRoles: Record<string, string>;
  accessToken: string | null;
  /**
   * What the caller holds in each of the nine services, as those services
   * answered — the input the navigation and the console's own sections gate on.
   *
   * A service **absent** from this map is one whose answer is not known, and the
   * console treats that as visible: hiding on a failed read turns a network blip
   * into a console missing half its destinations, and the backend refuses the
   * call either way. Never an authorization decision (PRD-TDD §11.4).
   */
  capabilities: CapabilityMap;
  /**
   * True once the capability sweep has **settled**, whatever it returned.
   *
   * `capabilities` alone cannot answer this: nine services all failing and nine
   * services all answering with a name this bundle does not know both produce an
   * empty map, and so does a session that has not swept yet. Without the flag the
   * guard either re-sweeps on every navigation or never sweeps after a sign-in.
   */
  capabilitiesLoaded: boolean;
  /** True once a bootstrap attempt has settled, whatever its outcome. */
  hydrated: boolean;

  setSession: (session: {
    profile: SessionProfile;
    role: AccountRole;
    adminRoles?: Record<string, string>;
    accessToken: string;
  }) => void;
  /**
   * Identity without a token. `POST /api/v1/tokens:refresh` returns
   * `{access_token, expires_in}` and **nothing else**, so a cold tab learns who
   * it is from `GET /api/v1/me` on the token the refresh just set — two calls,
   * two writes.
   */
  setIdentity: (identity: {
    profile: SessionProfile;
    role: AccountRole;
    adminRoles?: Record<string, string>;
  }) => void;
  setAccessToken: (accessToken: string) => void;
  setCapabilities: (capabilities: CapabilityMap) => void;
  markHydrated: () => void;
  clear: () => void;
}

export const useSessionStore = create<SessionState>((set) => ({
  profile: null,
  role: null,
  adminRoles: {},
  accessToken: null,
  capabilities: {},
  capabilitiesLoaded: false,
  hydrated: false,

  setSession: ({ profile, role, adminRoles, accessToken }) =>
    set({ profile, role, adminRoles: adminRoles ?? {}, accessToken, hydrated: true }),
  setIdentity: ({ profile, role, adminRoles }) =>
    set({ profile, role, adminRoles: adminRoles ?? {}, hydrated: true }),
  setAccessToken: (accessToken) => set({ accessToken }),
  setCapabilities: (capabilities) => set({ capabilities, capabilitiesLoaded: true }),
  markHydrated: () => set({ hydrated: true }),
  clear: () =>
    set({
      profile: null,
      role: null,
      adminRoles: {},
      accessToken: null,
      capabilities: {},
      capabilitiesLoaded: false,
      hydrated: true,
    }),
}));

/** Only these two reach the console. A `user` holding neither is turned away. */
export const CONSOLE_ROLES: readonly AccountRole[] = ['operator', 'admin'];

export function holdsConsoleRole(role: AccountRole | null): boolean {
  return role !== null && CONSOLE_ROLES.includes(role);
}
